/**
 * Generic admin list + mutation hooks for Foundation entities (P15.1).
 *
 * One generic hook family parameterised by the EntityAdapter so each
 * concrete entity (Vendor, IBSService, ...) doesn't need its own
 * useVendors / useCreateVendor / useUpdateVendor / useDeleteVendor
 * boilerplate. Backend endpoints follow a uniform shape:
 *
 *   GET    {endpoint}?limit=&offset=  -- paginated list
 *   POST   {endpoint}                 -- create, returns Row
 *   PATCH  {endpoint}/{id}            -- partial update, returns Row
 *   DELETE {endpoint}/{id}            -- 204
 *
 * The hooks invalidate `adapter.queryKey` on every mutation so the
 * list view refetches transparently.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

import type { EntityAdapter } from "./types";

/** One page of admin rows. */
export interface AdminPage<TRow> {
  rows: TRow[];
  offset: number;
  limit: number;
  /** Round 13 C4: how many rows the register holds, from X-Total-Count.
   *  Null when the server did not say -- which is NOT zero. */
  total: number | null;
}

/**
 * Read one page of admin rows.
 *
 * Backend default page is 100 rows; the page-up "Load more" wiring
 * lives in `AdminListView`. Keyed by (queryKey, offset, limit) so
 * each page caches independently + the list view can concatenate.
 */
/**
 * One page's query, as options.
 *
 * Round 13 C5 -- extracted so the register can ask for every page it has
 * loaded rather than the first and the most recent. Pressing "Load more"
 * twice used to show rows 1-100 and 201-300, with 101-200 gone and
 * unreachable without a reload, because the view concatenated exactly
 * two pages.
 */
export function adminListQueryOptions<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
  opts: { offset: number; limit: number; q?: string },
) {
  const q = opts.q?.trim() ?? "";
  return {
    queryKey: [...adapter.queryKey, "list", opts.offset, opts.limit, q] as const,
    queryFn: async (): Promise<AdminPage<TRow>> => {
      // Round 13 C7 -- the search goes to the server. Filtering the
      // hundred rows already fetched made "No services match" a claim
      // about the bank drawn from one page.
      const query = q ? `&q=${encodeURIComponent(q)}` : "";
      // Round 13 C4 -- and how many rows the register actually holds.
      const { rows, total } = await api.getList<TRow[]>(
        `${adapter.endpoint}?limit=${opts.limit}&offset=${opts.offset}${query}`,
      );
      return { rows, offset: opts.offset, limit: opts.limit, total };
    },
    staleTime: 15_000,
  };
}

export function useFoundationAdminList<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
  opts: { offset: number; limit: number },
): UseQueryResult<AdminPage<TRow>, ApiError> {
  return useQuery(adminListQueryOptions(adapter, opts));
}

/** Fetch a single row by id. Used by the edit drawer. */
export function useFoundationAdminDetail<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
  id: string | undefined,
): UseQueryResult<TRow, ApiError> {
  return useQuery({
    queryKey: [...adapter.queryKey, "detail", id ?? "_disabled"] as const,
    queryFn: () => api.get<TRow>(`${adapter.endpoint}/${id}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** POST create. */
export function useCreateFoundationEntity<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
): UseMutationResult<TRow, ApiError, TIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TIn) => api.post<TRow>(adapter.endpoint, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adapter.queryKey });
    },
  });
}

/** PATCH update. */
export function usePatchFoundationEntity<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
): UseMutationResult<TRow, ApiError, { id: string; body: TPatch }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => api.patch<TRow>(`${adapter.endpoint}/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adapter.queryKey });
    },
  });
}

/** DELETE (204 no-content). */
export function useDeleteFoundationEntity<TRow, TIn, TPatch>(
  adapter: EntityAdapter<TRow, TIn, TPatch>,
): UseMutationResult<void, ApiError, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.delete<void>(`${adapter.endpoint}/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adapter.queryKey });
    },
  });
}
